import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { COLORS } from "components/v3/Theme/colors";
import HeroShapesImg from "assets/images/HeroShapes.svg";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GLButton from "components/v3/sharedComponents/GLButton";
import GLTooltip from "components/v3/sharedComponents/GLTooltip";

type WelcomeHeroProps = {
  contentButtonText?: string;
  contentHeaderText?: string;
  contentSubText?: string;
  contentSubTextIcon?: string;
  contentParagraphText?: string;
  headerIcon: string;
  headerSubText: string;
  headerText: string;
  id?: string;
  onActionClick?: () => void;
  tooltipContent?: React.ReactElement<any, any>;
};

const WelcomeHero: React.FC<WelcomeHeroProps> = ({
  contentButtonText = "",
  contentHeaderText = "",
  contentSubText = "",
  contentSubTextIcon = "",
  contentParagraphText = "",
  headerIcon,
  headerText = "",
  headerSubText = "",
  id = "",
  onActionClick,
  tooltipContent = null
}) => {
  const hasContent = !!(
    contentButtonText?.length > 0 &&
    contentHeaderText?.length > 0 &&
    contentSubText?.length > 0 &&
    contentParagraphText?.length > 0
  );
  const hasTooltipContent = !!tooltipContent;

  return (
    <Box id={id}>
      {/* Header content */}
      <Box
        onClick={onActionClick}
        sx={{
          alignItems: "center",
          backgroundColor: COLORS.PRIMARY,
          borderWidth: "1px",
          borderColor: COLORS.PRIMARY,
          borderStyle: "solid",
          borderBottom: 0,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          color: "white",
          display: "flex",
          padding: {
            xs: "8px 16px 8px 8px",
            md: "10px 24px 10px 16px"
          },
          justifyContent: "space-between",
          ...(hasContent && {
            cursor: "pointer"
          }),
          ...(!hasContent && {
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4
          })
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            gap: 2
          }}
        >
          <Box
            component="img"
            sx={{
              height: {
                xs: 40,
                md: 50
              },
              width: {
                xs: 40,
                md: 50
              }
            }}
            src={headerIcon}
          />
          <Box>
            <Typography
              sx={{
                typography: {
                  xs: "body_strong_white",
                  md: "body_strong_lg_white"
                }
              }}
            >
              {headerText}
            </Typography>
            <Typography
              sx={{
                fontStyle: "italic",
                typography: {
                  xs: "body_xs_white",
                  md: "body_sm_white"
                }
              }}
              dangerouslySetInnerHTML={{
                __html: headerSubText
              }}
            />
          </Box>
        </Box>
        {hasTooltipContent && (
          <GLTooltip placement="left-start" title={tooltipContent}>
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" sx={{ color: COLORS.WHITE }} />
            </IconButton>
          </GLTooltip>
        )}
      </Box>
      {/* Main content */}
      {hasContent && (
        <Box
          sx={{
            paddingBottom: 1,
            paddingTop: 0,
            paddingX: 0
          }}
        >
          <Box
            sx={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <Box
              sx={{
                backgroundColor: COLORS.WHITE,
                borderWidth: "1px",
                borderColor: COLORS.PRIMARY,
                borderStyle: "solid",
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
                borderX: 0,
                position: "relative",
                overflow: "hidden"
              }}
            >
              {/* Decorative shapes */}
              <Box
                sx={{
                  border: 0,
                  position: "absolute",
                  left: {
                    xs: "-48px",
                    md: "-15px"
                  },
                  top: {
                    xs: "-10px",
                    md: 0
                  },
                  zIndex: 0
                }}
              >
                <Box
                  component="img"
                  sx={{
                    border: 0,
                    height: {
                      xs: "100px",
                      lg: "186px"
                    },
                    objectFit: "contain",
                    width: {
                      xs: "104px",
                      lg: "260px"
                    }
                  }}
                  src={HeroShapesImg}
                  alt=""
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  justifyContent: "center",
                  textAlign: "center",
                  py: { xs: 4, md: 8 },
                  mx: "auto",
                  width: "100%",
                  position: "relative"
                }}
              >
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    padding: 0
                  }}
                >
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      flexDirection: "column",
                      gap: "15px",
                      padding: {
                        xs: 1,
                        md: 0
                      }
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {!!contentSubTextIcon && (
                        <Typography
                          component="h2"
                          sx={{
                            typography: {
                              xs: "body_strong_lg_white",
                              md: "body_strong_xl_white"
                            }
                          }}
                        >
                          {contentSubTextIcon}
                        </Typography>
                      )}
                      <Typography
                        component="h2"
                        sx={{
                          background: `linear-gradient(90deg, ${COLORS.ACCENT3}, ${COLORS.PRIMARY})`,
                          backgroundClip: "text",
                          typography: {
                            xs: "body_strong_lg_white",
                            md: "body_strong_xl_white"
                          },
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent"
                        }}
                      >
                        {contentSubText}
                      </Typography>
                    </Box>
                    <Typography
                      component="h3"
                      sx={{
                        typography: {
                          xs: "h5_black",
                          md: "h3_black"
                        }
                      }}
                    >
                      {contentHeaderText}
                    </Typography>
                  </Box>
                  <Typography variant="body_black" sx={{ paddingX: "20px" }}>
                    {contentParagraphText}
                  </Typography>
                </Box>
                <GLButton
                  id="welcome-hero-button"
                  buttonType="primary"
                  onClick={onActionClick}
                  rightIcon={<ArrowForwardIcon />}
                >
                  <Typography
                    sx={{
                      typography: {
                        xs: "body_strong_white",
                        md: "body_strong_lg_white"
                      }
                    }}
                  >
                    {contentButtonText}
                  </Typography>
                </GLButton>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WelcomeHero;
